import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Link } from 'react-router-dom';
import { UseFormRegister, UseFormSetValue, UseFormWatch, FieldErrors } from 'react-hook-form';

interface ConsentCheckboxesProps {
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  errors: FieldErrors<any>;
}

export const ConsentCheckboxes: React.FC<ConsentCheckboxesProps> = ({
  setValue,
  watch,
  errors,
}) => {
  const consentEmail = watch('consentEmail');
  const consentSms = watch('consentSms');
  const acceptTerms = watch('acceptTerms');

  return (
    <div className="space-y-3 border-t border-border pt-4">
      {/* Terms & Conditions Acceptance */}
      <div className="flex items-start gap-2">
        <Checkbox
          id="accept-terms"
          checked={acceptTerms || false}
          onCheckedChange={(checked) => setValue('acceptTerms', checked === true, { shouldValidate: true })}
          className="mt-0.5"
        />
        <Label htmlFor="accept-terms" className="text-xs text-background/70 leading-relaxed cursor-pointer">
          I agree to the{' '}
          <Link to="/terms-of-service" className="underline hover:text-primary" target="_blank">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy-policy" className="underline hover:text-primary" target="_blank">Privacy Policy</Link>. *
        </Label>
      </div>
      {errors.acceptTerms && (
        <p className="text-xs text-destructive ml-6">{errors.acceptTerms.message as string}</p>
      )}

      {/* Email Consent */}
      <div className="flex items-start gap-2">
        <Checkbox
          id="consent-email"
          checked={consentEmail || false}
          onCheckedChange={(checked) => setValue('consentEmail', checked === true, { shouldValidate: true })}
          className="mt-0.5"
        />
        <Label htmlFor="consent-email" className="text-xs text-background/70 leading-relaxed cursor-pointer">
          I consent to receive email communications regarding my inquiry. *
        </Label>
      </div>
      {errors.consentEmail && (
        <p className="text-xs text-destructive ml-6">{errors.consentEmail.message as string}</p>
      )}

      {/* Explicit SMS Opt-In (TCPA compliant — unchecked by default) */}
      <div className="flex items-start gap-2">
        <Checkbox
          id="consent-sms"
          checked={consentSms || false}
          onCheckedChange={(checked) => setValue('consentSms', checked === true)}
          className="mt-0.5"
        />
        <Label htmlFor="consent-sms" className="text-xs text-background/70 leading-relaxed cursor-pointer">
          I consent to receive SMS/text messages at the phone number provided. Message frequency varies. Message & data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of purchase.
        </Label>
      </div>

      {/* SMS/Messaging Disclosure */}
      <p className="text-[10px] text-background/50 ml-6 leading-relaxed">
        By providing your phone number and checking the SMS box above, you authorize us to send text messages for service updates, appointment reminders, and promotional offers. 
        SMS data is collected, stored, and used in accordance with our{' '}
        <Link to="/privacy-policy" className="underline hover:text-primary" target="_blank">Privacy Policy</Link>. 
        We do not share your phone number or SMS consent data with third parties for marketing purposes.
      </p>
    </div>
  );
};
